import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@mui/material";
import { Plus, Video, Copy, Edit2, XCircle, Check, X, CheckCircle2, Repeat, Search, Loader2 } from "lucide-react";
import styles from "./hiralityMeetings.module.scss";
import MeetingForm from "./MeetingForm";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";
import { apiBaseUrl } from "../../../../../../services/config";
import { MEETING_TYPES, getMeetingType } from "./meetingTypes";

const API = apiBaseUrl;
export const AVATAR_COLORS = ["#00C8FF","#7C5CFF","#00E5A3","#FFB800","#FF4D6D","#3B82F6","#A855F7"];
export const STATUS_BADGE = { Completed: "green", Cancelled: "red" };

export const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token") || ""}`,
});

export const getStoredUser = () => {
  try { return JSON.parse(sessionStorage.getItem("platform_user") || localStorage.getItem("platform_user") || "{}"); }
  catch { return {}; }
};

export const buildMeetingLink = (m) => {
  const base  = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
  const token = sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token") || "";
  const user  = getStoredUser();
  const displayName = user.full_name || user.first_name || "Guest";
  // type=meeting is fixed — it picks which SessionRoom body renders (always
  // MeetingBody for a Hirality Meeting). meetingType is the separate, purely
  // cosmetic label (see meetingTypes.js) that tunes the dock's copy and the
  // AI summary's angle inside that body — never which body renders.
  const params = `type=meeting&meetingType=${m.meeting_type || "meeting"}&roomId=${m.room_name}&meetingId=${m.id}&token=${encodeURIComponent(token)}&displayName=${encodeURIComponent(displayName)}`;
  return `${base}/session-room?${params}`;
};

export default function Meetings() {
  const { T } = useTheme();
  const navigate = useNavigate();
  const [tab,       setTab]       = useState("upcoming"); // upcoming | history
  const [typeTab,   setTypeTab]   = useState("all");       // all | meeting | ekyc | interview | healthcare
  const [upcoming,  setUpcoming]  = useState([]);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [created,       setCreated]     = useState(null); // meeting just scheduled — show its link
  const [editOpen,   setEditOpen]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [cancelId,   setCancelId]   = useState(null);
  const [copiedId,   setCopiedId]   = useState(null);

  // Semantic search — searches what was actually discussed (AI summaries),
  // not just title/date. Only completed meetings with a transcript are
  // searchable; replaces the tab-filtered table with ranked results while active.
  const [searchInput,   setSearchInput]   = useState("");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching,     setSearching]     = useState(false);
  const searchActive = searchQuery.trim().length > 0;

  const runSearch = async (q) => {
    const query = q.trim();
    setSearchQuery(query);
    if (!query) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/hirality-meetings/search`, { params: { q: query }, headers: authHeader() });
      setSearchResults(r.data || []);
    } catch (e) {
      console.error("Search failed:", e);
      setSearchResults([]);
    } finally { setSearching(false); }
  };

  const clearSearch = () => { setSearchInput(""); setSearchQuery(""); setSearchResults([]); };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [u, h] = await Promise.all([
        axios.get(`${API}/hirality-meetings/upcoming`, { headers: authHeader() }),
        axios.get(`${API}/hirality-meetings/history`,  { headers: authHeader() }),
      ]);
      setUpcoming(u.data || []);
      setHistory(h.data || []);
    } catch (e) { console.error("Failed to load meetings:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const startMeeting = async (m) => {
    try { await axios.post(`${API}/hirality-meetings/${m.id}/start`, {}, { headers: authHeader() }); }
    catch (e) { console.warn("start ping failed:", e.message); }
    window.open(buildMeetingLink(m), "_blank", "noopener,noreferrer");
  };

  const copyLink = (m) => {
    navigator.clipboard.writeText(buildMeetingLink(m)).then(() => {
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const confirmCancel = async () => {
    try {
      await axios.put(`${API}/hirality-meetings/${cancelId}`, { status: "Cancelled" }, { headers: authHeader() });
      setCancelId(null);
      fetchAll();
    } catch (e) { console.error("Cancel failed:", e); }
  };

  const closeScheduleModal = () => { setScheduleOpen(false); setCreated(null); };
  const viewDetail = (m) => navigate(`/admin/hirality-meetings/${m.id}`);

  const statusRows = tab === "upcoming" ? upcoming : history;
  const rows = typeTab === "all" ? statusRows : statusRows.filter(m => (m.meeting_type || "meeting") === typeTab);
  const typeCounts = MEETING_TYPES.reduce((acc, t) => {
    acc[t.key] = statusRows.filter(m => (m.meeting_type || "meeting") === t.key).length;
    return acc;
  }, {});

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>HIRALITY MEETINGS</div>
          <h2><Video size={20} style={{ verticalAlign: "middle", marginRight: 8 }}/>Meetings</h2>
          <p>Every Hirality Meetings call — scheduled, live, or past</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnPrimary} onClick={() => setScheduleOpen(true)}>
            <Plus size={14}/> Schedule Meeting
          </button>
        </div>
      </div>

      {/* Semantic search — searches what was actually discussed, not just title/date */}
      <div style={{ position: "relative", marginBottom: 16, maxWidth: 440 }}>
        <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
        <input value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runSearch(searchInput); }}
          placeholder="Search what was discussed in past meetings…"
          className={styles.formInput}
          style={{ paddingLeft: 34, paddingRight: searchInput ? 34 : 12 }} />
        {searchInput && (
          <button onClick={clearSearch}
            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.muted }}>
            <X size={14} />
          </button>
        )}
      </div>

      {searchActive ? (
        <div className={styles.card}>
          {searching ? (
            <div className={styles.loader}><div className={styles.spinner}/>Searching…</div>
          ) : searchResults.length === 0 ? (
            <div className={styles.emptyRow} style={{ padding: 40, textAlign: "center", color: T.muted, fontSize: 13 }}>
              No meetings found discussing “{searchQuery}”.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {searchResults.map((r) => {
                const mt = getMeetingType(r.meeting_type || "meeting");
                const c = T[mt.color];
                const Icon = mt.icon;
                return (
                  <div key={r.id} onClick={() => viewDetail(r)}
                    style={{ padding: "14px 20px", borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 700, color: T.text, fontSize: 13.5 }}>{r.title}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 600,
                        color: c, background: `${c}18`, borderRadius: 20, padding: "2px 7px" }}>
                        <Icon size={9} /> {mt.label}
                      </span>
                      <span style={{ fontSize: 11, color: T.dim }}>{r.meeting_date}</span>
                    </div>
                    {r.summary && (
                      <p style={{ fontSize: 12, color: T.muted, margin: "6px 0 0", lineHeight: 1.6, maxWidth: 700 }}>{r.summary}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* Meeting-type tabs — primary navigation */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button onClick={() => setTypeTab("all")}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8,
            fontSize: 12.5, fontWeight: 600, fontFamily: "Outfit,sans-serif", cursor: "pointer",
            border: `1px solid ${typeTab === "all" ? T.cyan : T.border}`,
            background: typeTab === "all" ? `${T.cyan}18` : "transparent",
            color: typeTab === "all" ? T.cyan : T.muted }}>
          All ({statusRows.length})
        </button>
        {MEETING_TYPES.map(({ key, label, icon: Icon, color }) => {
          const active = typeTab === key;
          const c = T[color];
          return (
            <button key={key} onClick={() => setTypeTab(key)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8,
                fontSize: 12.5, fontWeight: 600, fontFamily: "Outfit,sans-serif", cursor: "pointer",
                border: `1px solid ${active ? c : T.border}`,
                background: active ? `${c}18` : "transparent",
                color: active ? c : T.muted }}>
              <Icon size={13} /> {label} ({typeCounts[key]})
            </button>
          );
        })}
      </div>

      {/* Status sub-tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { key: "upcoming", label: `Upcoming (${typeTab === "all" ? upcoming.length : upcoming.filter(m => (m.meeting_type||"meeting")===typeTab).length})` },
          { key: "history",  label: `History (${typeTab === "all" ? history.length : history.filter(m => (m.meeting_type||"meeting")===typeTab).length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: "8px 16px", borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              fontFamily: "Outfit,sans-serif", cursor: "pointer",
              border: `1px solid ${tab === t.key ? T.cyan : T.border}`,
              background: tab === t.key ? `${T.cyan}18` : "transparent",
              color: tab === t.key ? T.cyan : T.muted,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={styles.card}>
        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr>
                  <th>Meeting</th><th>Date & Time</th><th>Duration</th>
                  <th>Host</th>
                  {tab === "upcoming" ? <th>Participants</th> : <th>Status</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={6}>
                    {tab === "upcoming"
                      ? `No upcoming${typeTab === "all" ? "" : ` ${getMeetingType(typeTab).label.toLowerCase()}`} meetings — click "Schedule Meeting" to create one.`
                      : `No past${typeTab === "all" ? "" : ` ${getMeetingType(typeTab).label.toLowerCase()}`} meetings yet.`}
                  </td></tr>
                ) : rows.map(m => (
                  <tr key={m.id} style={{ cursor: "pointer" }} onClick={() => viewDetail(m)}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
                        {m.title}
                        {typeTab === "all" && (() => {
                          const mt = getMeetingType(m.meeting_type || "meeting");
                          const c = T[mt.color];
                          const Icon = mt.icon;
                          return (
                            <span title={mt.label}
                              style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 600,
                                color: c, background: `${c}18`, borderRadius: 20, padding: "2px 7px" }}>
                              <Icon size={9} /> {mt.label}
                            </span>
                          );
                        })()}
                        {m.series_id && (
                          <span title={`Recurring · ${m.recurrence_frequency || ""}`}
                            style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 600,
                              color: T.purple, background: `${T.purple}18`, borderRadius: 20, padding: "2px 7px" }}>
                            <Repeat size={9} /> {m.recurrence_frequency}
                          </span>
                        )}
                      </div>
                      {m.description && (
                        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2, maxWidth: 240, whiteSpace: "normal" }}>
                          {m.description}
                        </div>
                      )}
                    </td>
                    <td className="dim">{m.meeting_date} · {m.meeting_time}</td>
                    <td className="dim">{m.duration_minutes} min</td>
                    <td className="dim">{m.host_name || "—"}</td>

                    {tab === "upcoming" ? (
                      <td>
                        {m.participants?.length ? (
                          <div className={styles.avatarGroup} title={m.participants.map(p => p.name || p.email).join(", ")}>
                            {m.participants.slice(0, 4).map((p, i) => (
                              <div key={i} className={styles.avatar} style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                                {(p.name || p.email || "?").slice(0, 1).toUpperCase()}
                              </div>
                            ))}
                            {m.participants.length > 4 && (
                              <div className={styles.avatar} style={{ background: T.dim }}>+{m.participants.length - 4}</div>
                            )}
                          </div>
                        ) : <span style={{ color: T.muted }}>—</span>}
                      </td>
                    ) : (
                      <td><span className={`${styles.badge} ${styles[STATUS_BADGE[m.status] || "grey"]}`}>{m.status}</span></td>
                    )}

                    <td onClick={(e) => e.stopPropagation()}>
                      {tab === "upcoming" ? (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button className={styles.btnSm} style={{ color: T.green, borderColor: `${T.green}40` }}
                            onClick={() => startMeeting(m)}>
                            <Video size={11}/> Start
                          </button>
                          <button className={styles.btnSm} onClick={() => copyLink(m)}>
                            {copiedId === m.id ? <Check size={11}/> : <Copy size={11}/>}
                            {copiedId === m.id ? "Copied" : "Copy Link"}
                          </button>
                          <button className={styles.btnSm} onClick={() => { setEditing(m); setEditOpen(true); }}>
                            <Edit2 size={11}/> Edit
                          </button>
                          <button className={styles.btnSm} style={{ color: T.red, borderColor: `${T.red}40` }}
                            onClick={() => setCancelId(m.id)}>
                            <XCircle size={11}/> Cancel
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: T.muted }} onClick={() => viewDetail(m)}>Details →</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* ── Schedule Meeting modal (centered by default) ── */}
      <Dialog open={scheduleOpen} onClose={closeScheduleModal}>
        <div style={{ background: T.card, border: `1px solid ${T.cyan}33`, borderRadius: 14, width: 520, fontFamily: "Outfit,sans-serif", overflow: "hidden" }}>
          <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, background: `${T.cyan}0A`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: T.text }}>
              {created ? "Meeting Scheduled" : "Schedule a Meeting"}
            </h3>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: T.muted }} onClick={closeScheduleModal}><X size={16}/></button>
          </div>
          <div style={{ padding: 24 }}>
            {created ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <CheckCircle2 size={22} style={{ color: T.green }}/>
                  <div>
                    <div style={{ fontWeight: 700, color: T.text }}>{created.title}</div>
                    <div style={{ fontSize: 12, color: T.muted }}>{created.meeting_date} · {created.meeting_time} ({created.duration_minutes} min)</div>
                  </div>
                </div>
                <div>
                  <label className={styles.formLabel}>Join Link</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input readOnly value={buildMeetingLink(created)} className={styles.formInput}
                      onFocus={e => e.target.select()} />
                    <button type="button" className={styles.btnSecondary}
                      onClick={() => {
                        navigator.clipboard.writeText(buildMeetingLink(created));
                        setCopiedId("created");
                        setTimeout(() => setCopiedId(null), 2000);
                      }}>
                      {copiedId === "created" ? <Check size={13}/> : <Copy size={13}/>}
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8 }}>
                  <button type="button" className={styles.btnSecondary} onClick={closeScheduleModal}>Done</button>
                  <button type="button" className={styles.btnPrimary} onClick={() => startMeeting(created)}>
                    <Video size={13}/> Start Now
                  </button>
                </div>
              </div>
            ) : (
              <MeetingForm
                header="Schedule"
                onCloseDialog={closeScheduleModal}
                onSaved={(data) => { setCreated(data); fetchAll(); }}
              />
            )}
          </div>
        </div>
      </Dialog>

      {/* ── Edit modal ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <div style={{ background: T.card, border: `1px solid ${T.cyan}33`, borderRadius: 14, width: 520, fontFamily: "Outfit,sans-serif", overflow: "hidden" }}>
          <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, background: `${T.cyan}0A`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: T.text }}>Edit Meeting</h3>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: T.muted }} onClick={() => setEditOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding: 24 }}>
            <MeetingForm
              meetingData={editing}
              header="Edit"
              onCloseDialog={() => setEditOpen(false)}
              onSaved={() => { setEditOpen(false); fetchAll(); }}
            />
          </div>
        </div>
      </Dialog>

      <Popup
        open={!!cancelId}
        title="Cancel Meeting"
        message="Cancel this meeting? Participants will need to be notified separately."
        handleClose={() => setCancelId(null)}
        handleConfirm={confirmCancel}
        type="alert"
      />
    </div>
  );
}
